<template>
  <div
    class="group relative bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border-2"
    :class="[
      isDragging ? 'opacity-50 border-blue-500 border-dashed' : 'border-gray-100 dark:border-gray-700',
      'cursor-move'
    ]"
    :draggable="true"
    @dragstart="$emit('drag-start')"
    @dragover="$emit('drag-over', $event)"
    @drop="$emit('drop')"
    @dragend="$emit('drag-end')"
  >
    <!-- Drag Handle Overlay (Visible on Hover) -->
    <div class="absolute top-2 left-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
      <div class="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-1.5 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600">
        <Bars3Icon class="w-5 h-5 text-gray-500" />
      </div>
    </div>

    <!-- Quick Actions (Visible on Hover) -->
    <div class="absolute top-2 right-2 z-20 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
      <button 
        @click.stop="$emit('toggle-favorite')"
        type="button"
        class="p-2 sm:p-1.5 rounded-lg shadow-sm border transition-colors bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
        :class="project.isFavorite ? 'text-yellow-500 border-yellow-200' : 'text-gray-400 border-gray-200 dark:border-gray-600 hover:text-yellow-500'"
        :title="project.isFavorite ? 'Von Favoriten entfernen' : 'Als Favorit markieren'"
      >
        <StarIcon class="w-5 h-5" :class="{ 'fill-current': project.isFavorite }" />
      </button>
      <button 
        @click.stop="$emit('edit')"
        type="button"
        class="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-2 sm:p-1.5 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 text-blue-600 hover:text-blue-700 transition-colors min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
        title="Bearbeiten"
      >
        <PencilSquareIcon class="w-5 h-5" />
      </button>
      <button 
        @click.stop="$emit('delete')"
        type="button"
        class="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-2 sm:p-1.5 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 text-red-600 hover:text-red-700 transition-colors min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
        title="Löschen"
      >
        <TrashIcon class="w-5 h-5" />
      </button>
    </div>

    <!-- Project Content -->
    <div class="relative h-40 bg-gray-100 dark:bg-gray-900">
      <img
        v-if="projectImage"
        :src="projectImage"
        :alt="project.title"
        class="w-full h-full object-contain p-2"
      />
      <div v-else class="w-full h-full flex items-center justify-center">
        <PhotoIcon class="w-8 h-8 text-gray-400" />
      </div>
      
      <!-- Accent Color Line -->
      <div 
        class="absolute bottom-0 left-0 right-0 h-1"
        :style="{ backgroundColor: project.accentColor || '#3B82F6' }"
      ></div>
    </div>

    <div class="p-4">
      <div class="flex items-center space-x-2 mb-1">
        <h3 class="font-bold text-gray-900 dark:text-white truncate flex-1" @dblclick="$emit('edit-title')">
          {{ project.title }}
        </h3>
        <span v-if="project.id?.startsWith('auto-')" class="text-[10px] text-blue-500 font-medium">Auto</span>
      </div>
      <p class="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 h-8">
        {{ project.description }}
      </p>
      
      <div class="mt-3 flex flex-wrap gap-1">
        <span 
          v-for="tech in project.technologies.slice(0, 3)" 
          :key="tech"
          class="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[10px] font-medium text-gray-600 dark:text-gray-300"
        >
          {{ tech }}
        </span>
        <span v-if="project.technologies.length > 3" class="text-[10px] text-gray-400 self-center">
          +{{ project.technologies.length - 3 }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { 
  Bars3Icon, 
  StarIcon, 
  PencilSquareIcon, 
  TrashIcon,
  PhotoIcon 
} from '@heroicons/vue/24/outline'
import { useTheme } from '../../composables/useTheme'
import type { PortfolioProject } from '../../firebase/portfolio'

const props = defineProps<{
  project: PortfolioProject
  isDragging?: boolean
}>()

const { isDark } = useTheme()

// Bestimme das zu verwendende Projektbild: Logo (wenn vorhanden) oder Screenshot
const projectImage = computed(() => {
  // Wenn Logos vorhanden sind, verwende das passende Logo basierend auf Theme
  if (isDark.value && props.project.logoDark) {
    return props.project.logoDark
  }
  if (!isDark.value && props.project.logoLight) {
    return props.project.logoLight
  }
  // Fallback auf Screenshot
  if (props.project.screenshots && props.project.screenshots.length > 0) {
    return props.project.screenshots[0]
  }
  return null
})

defineEmits(['drag-start', 'drag-over', 'drop', 'drag-end', 'edit', 'delete', 'toggle-favorite', 'edit-title'])
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.cursor-move {
  cursor: grab;
}

.cursor-move:active {
  cursor: grabbing;
}
</style>

